import Link from 'next/link';
import type { ComponentProps, ReactNode } from 'react';
import { Icon, type IconName } from '@/components/ui/Icon';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'outline' | 'ghostDark' | 'solidLight';
type Size = 'sm' | 'md' | 'lg';

const base =
  'group/btn inline-flex items-center justify-center gap-2.5 rounded-full font-bold whitespace-nowrap ' +
  'transition-[background-color,border-color,color,box-shadow,transform] duration-200 ' +
  'ease-(--ease-out-quart) active:translate-y-px';

const variants: Record<Variant, string> = {
  /** Solid brand — the single strongest action on a surface. */
  primary:
    'bg-brand-600 text-white shadow-[0_10px_24px_-10px_rgb(22_104_224/0.7)] ' +
    'hover:bg-brand-700 hover:shadow-[0_14px_30px_-10px_rgb(22_104_224/0.75)]',
  /** Bordered, for light surfaces. */
  outline:
    'border border-line bg-white text-ink hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700',
  /** Translucent, for dark surfaces. */
  ghostDark:
    'border border-white/15 bg-white/8 text-white backdrop-blur-sm hover:border-white/30 hover:bg-white/14',
  /** White on dark surfaces. */
  solidLight: 'bg-white text-navy-900 hover:bg-brand-50',
};

const sizes: Record<Size, string> = {
  sm: 'h-11 px-5 text-[0.9375rem]',
  md: 'h-12 px-6 text-[0.9375rem]',
  lg: 'h-14 px-7 text-base',
};

type CommonProps = {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  /** Trailing icon; `arrowRight` slides on hover. */
  icon?: IconName;
  /** Leading icon. */
  iconLeft?: IconName;
  className?: string;
};

type ButtonAsLink = CommonProps & { href: string } & Omit<
    ComponentProps<typeof Link>,
    'href' | 'className' | 'children'
  >;

type ButtonAsButton = CommonProps & { href?: undefined } & Omit<
    ComponentProps<'button'>,
    'className' | 'children'
  >;

export function Button(props: ButtonAsLink | ButtonAsButton) {
  const {
    children,
    variant = 'primary',
    size = 'md',
    icon,
    iconLeft,
    className,
    ...rest
  } = props;

  const classes = cn(base, variants[variant], sizes[size], className);

  const inner = (
    <>
      {iconLeft ? <Icon name={iconLeft} size={18} className="shrink-0" /> : null}
      <span>{children}</span>
      {icon ? (
        <Icon
          name={icon}
          size={18}
          className={cn(
            'shrink-0 transition-transform duration-200 ease-(--ease-out-quart) motion-reduce:transition-none',
            icon === 'arrowRight' && 'group-hover/btn:translate-x-1',
            icon === 'arrowUpRight' &&
              'group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5',
          )}
        />
      ) : null}
    </>
  );

  if (typeof props.href === 'string') {
    const { href, ...linkRest } = rest as Omit<ButtonAsLink, keyof CommonProps>;
    return (
      <Link href={href} className={classes} {...linkRest}>
        {inner}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={classes}
      {...(rest as Omit<ButtonAsButton, keyof CommonProps>)}
    >
      {inner}
    </button>
  );
}
